import React from 'react';
import { Container, Typography, Box, Divider, CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
    typography: {
        fontFamily: '"League Spartan", sans-serif', // Your local font
    },
});

function PrivacyPolicy() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Privacy Policy
                </Typography>
                <Divider />

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Introduction
                    </Typography>
                    <Typography paragraph>
                        This privacy policy describes the practices of Kerdos Wallet ("we," "our," or "us") regarding the collection, utilization, sharing, and safeguarding of personal information gathered from users of our services, including our website, mobile applications, and associated products and services (collectively referred to as the "Services").
                    </Typography>
                    <Typography paragraph>
                        By accessing our Services, you acknowledge and accept the terms of this Policy and our Terms of Use, and you consent to the collection, use, disclosure, and retention of your information as outlined herein. If you do not agree with any aspect of this Privacy Policy or our Terms of Use, we kindly ask that you refrain from using our Services.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Information Collection
                    </Typography>
                    <Typography paragraph>
                        We get information about you using several techniques, including:
                    </Typography>
                    <Typography paragraph>
                        <strong>Information You Provide Us:</strong> This may encompass identity information (e.g., name, username), contact information (e.g., email address, phone number), profile details, financial information (e.g., credit card information), transaction details, usage data, marketing preferences, and technical information (e.g., Ethereum wallet address).
                    </Typography>
                    <Typography paragraph>
                        <strong>Information Collected by Infura:</strong> Your IP address and Ethereum wallet address are collected when you utilize Infura as your default RPC provider within Kerdos Wallet. However, this data is not collected if you access Kerdos using your own Ethereum node or an external RPC provider.
                    </Typography>
                    <Typography paragraph>
                        <strong>Further Information about Our Services:</strong> Additional information collected regarding our Services may include identity verification documents, Anti-Money Laundering (AML) and Know Your Customer (KYC) information, details regarding the source of funds, and information pertaining to token holdings.
                    </Typography>
                    <Typography paragraph>
                        <strong>Information We Obtain From Others:</strong> We may obtain information about you from third-party sources, which may be integrated with the information we collect through our services.
                    </Typography>
                    <Typography paragraph>
                        <strong>Information Automatically Collected:</strong> We may automatically record specific information regarding your usage of our sites (e.g., IP address, device type, pages visited) to enhance and improve our services
                    </Typography>
                    <Typography paragraph>
                        <strong>Cookies:</strong> We employ cookies and similar technologies to analyze trends, manage the Sites, monitor user activity, and collect demographic information. Users can manage their cookie preferences through their browser settings.

                    </Typography>
                </Box>


                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Use of Personal Information
                    </Typography>
                    <Typography paragraph>
                        We utilize your personal information for the following purposes:
                    </Typography>
                    <ul>
                        <li>Providing and enhancing our Services</li>
                        <li>Ensuring compliance with applicable laws and regulations</li>
                        <li>Keeping you informed about events, promotions, and other relevant updates</li>
                        <li>Optimizing our platform and user experience</li>
                        <li>Implementing fraud prevention and safety measures</li>
                        <li>Sharing testimonials or endorsements with your consent</li>
                    </ul>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Sharing of Personal Information
                    </Typography>
                    <Typography paragraph>
                        We do not share your personal information with third parties without your consent, except under specific circumstances, including:
                    </Typography>
                    <ul>
                        <li>With our affiliates and subsidiaries</li>
                        <li>In connection with business transfers or transactions</li>
                        <li>To comply with requests from law enforcement or to fulfill legal obligations</li>
                        <li>With professional advisors and service providers</li>
                        <li>With other companies or entities, provided we have your permission</li>
                        <li>Aggregated or anonymized data may be shared for analytical purposes.</li>
                    </ul>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        International Transfer
                    </Typography>
                    <Typography paragraph>
                        Your personal information may be transferred to or from locations outside of your jurisdiction, including the United States, where privacy laws may vary.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Security of Information
                    </Typography>
                    <Typography paragraph>
                        We implement industry-standard security protocols to protect data transmitted through our services. However, we cannot guarantee the security of data transmitted over the Internet.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Information Choices and Changes
                    </Typography>
                    <Typography paragraph>
                        You may access, update, correct, or delete your information by contacting us at info@kerdosapp.com. Additionally, you can manage your contact preferences and opt out of certain communications.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Eligibility
                    </Typography>
                    <Typography paragraph>
                        If you are a minor, you may use our Services only with the consent of your parent or legal guardian.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Marketing Communications and Sharing
                    </Typography>
                    <Typography paragraph>
                        You may opt out of marketing communications by contacting us or following the opt-out instructions provided in commercial emails. However, certain non-marketing communications may continue to be sent.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Third-Party Links and Websites
                    </Typography>
                    <Typography paragraph>
                        Our Privacy Notice does not extend to the privacy practices of third-party websites linked from our Sites.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Co-Branded Websites
                    </Typography>
                    <Typography paragraph>
                        Our Privacy Notice does not apply to co-branded websites. We encourage visitors to review the privacy notices on those individual websites.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Changes to this Privacy Policy
                    </Typography>
                    <Typography paragraph>
                        We may update this Privacy Policy from time to time. By continuing to use our services, you acknowledge and accept any such updates.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Data Protection Officer
                    </Typography>
                    <Typography paragraph>
                        For inquiries or concerns regarding data protection, please contact our Data Protection Officer at info@kerdosapp.com.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Contact Us
                    </Typography>
                    <Typography paragraph>
                        If you have any concerns or complaints regarding this policy, please contact us at info@kerdosapp.com.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Notice to EU Data Subjects
                    </Typography>
                    <Typography paragraph>
                        EU data subjects have rights under the General Data Protection Regulation (GDPR) concerning their personal information. You may request actions related to your personal information or file complaints with us or your data protection authority.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Cross-Border Data Transfer
                    </Typography>
                    <Typography paragraph>
                        Your information may be transferred to the United States or other countries, provided that appropriate safeguards are in place.
                    </Typography>
                </Box>
            </Container>
        </ThemeProvider>
    );
}

export default PrivacyPolicy;
